import { DropdownOption } from 'components/final-form/dropdownUtils';
import { MessageSourceProps, withMessages } from 'react-message-source';
import * as React from 'react';
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import styled from 'styled-components';
import { Button, Checkbox, Grid, Header } from 'semantic-ui-react';
import {
  AutomaticReminderResponse,
  AutomaticReminderSubmit,
  BillReminderResponse,
  BillReminderSearchResponse,
  MandateBankDto, MandateBillDto,
  PatientDto
} from 'ts-types/api.types';
import FormContent from 'components/layout/FormContent';
import PxGrid from 'components/layout/PxGrid';
import ErrorMessage from 'components/final-form/ErrorMessage';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import { FormApi } from 'final-form';
import { BillIconRequest, getBillIcon } from 'components/bill/utils/billUtils';
import { BillStatus, BillType } from 'ts-types/api.enums';
import { currencyFormatter, numberFormatter } from 'utils/fomattingUtils';
import { CancelTokenSource } from 'axios';
import axios from 'services/http';
import DatePicker from 'components/final-form/DatePicker';
import { getAllMandateBanksByMandateId } from 'services/mandateBankServices';
import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import { RouteComponentProps } from 'react-router';
import Select from 'components/final-form/Select';
import { CheckboxSc } from 'components/final-form/CheckBox';
import { withRouterWorkaround } from 'utils/workaroundUtils';
import LoaderComponent from 'components/LoaderComponent';
import createDecorator from 'final-form-calculate';
import { automaticRemind, getBillReminderPdfUrl } from 'services/billReminderServices';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import PdfModal from 'components/pdf-modal/PdfModal';
import { getAllMandateBillsByMandateId } from 'services/mandateBillServices';
import moment from 'moment';
import { applyStyles } from 'utils/textUtils';

const StyledFinalFormContainer = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  padding: 1em 1em;
`;

const StyledTable = styled(VirtualizedTable)`
  .ReactVirtualized__Table__Grid {
    outline: none;
    border: 1px solid rgba(34, 36, 38, .15);
    border-top: none;
    border-radius: 0 0 .28571429rem .28571429rem;
  }

  .ReactVirtualized__Table__row {
    border-left: none;
  }

  .ReactVirtualized__Table__headerColumn {
    height: 100%;
    border-right: 1px solid rgba(34, 36, 38, .15);
    padding-top: 0.85rem;
  }

  .ReactVirtualized__Table__headerColumn:last-child {
    border-right: none;
  }

  .ReactVirtualized__Table__rowColumn {
    height: 100%;
    border-right: 1px solid rgba(34, 36, 38, .15);
    padding-top: 0.85rem;
  }

  .ReactVirtualized__Table__rowColumn:last-child {
    border-right: none;
  }

  & .evenRow:hover, .oddRow:hover {
    background: rgba(0, 0, 0, .05);
  }
`;

const StyledForm = styled.form`
  display: flex;
  flex-flow: column;
  height: 100%;
`;

const StyledFormContent = styled.div`
  display: flex;
  flex-direction: row;
  margin-bottom: 15px;
  height: 100%;
`;

const StyledContainer = styled.div`

  flex: 1 1 auto;
  display: flex;
  flex-direction: column;

  .ui.header:not(h1):not(h2):not(h3):not(h4):not(h5):not(h6) {
    font-size: 1.1rem;
    color: #373737;

    &.small {
      font-size: 1.05em;
    }
  }

  &.margin {
    margin-left: 20px;
  }

  .row-header-data {
    margin-bottom: 1rem;
  }

  .header-data {
    margin-top: 0.2rem;
    font-weight: 300;
  }
`;

const TableContainer = styled.div`
  height: 100%;
  min-height: 300px;
`;

const ReminderDate = styled(Grid.Row)`
  padding-bottom: 0.25rem !important;
`;

const MandateBank = styled(Grid.Row)`
  padding-top: 0.25rem !important;
`;

const ReminderRow = styled(Grid.Row)`
  padding-top: 0.25rem !important;
  padding-bottom: 0.25rem !important;
`;

const TotalRemindedColumn = styled(Grid.Column)`
  padding-top: 0.5rem;
`;

const StyledSelectedBills = styled.span`
  float: right;
  text-align: right;
  font-weight: 300;
  font-size: 1rem;

  &.margin-top {
    margin-top: 0.5em;
  }

  .highlight-value {
    color: rgb(48 145 218);
    font-weight: bold;
  }
`;

const errorMessageStyle = {
  marginBottom: "1rem"
};

const activeRowStyle = {
  background: "#e0e0e0",
  color: "rgba(0,0,0,.87)",
  boxShadow: "0 0 0 rgba(0,0,0,.87) inset"
};

const numberColumnStyle = {
  floating: "right",
  paddingRight: "0.5rem"
};

enum AllChosenState {
  ALL,
  SOME,
  NONE
}

interface ExtendedAutomaticReminderResponse extends AutomaticReminderResponse {
  checked: boolean
}

interface FinalFormData {
  bills: Array<ExtendedAutomaticReminderResponse>,
  selected?: ExtendedAutomaticReminderResponse,
  reminderDate: string,
  mandateBankId: number,
  proofList: boolean
}

interface Props extends MessageSourceProps, AuthConsumerRenderProps, RouteComponentProps {
  bills: Array<AutomaticReminderResponse>,
  billsLoading: boolean,
  currentPatient?: PatientDto,
  onPatientRefresh: (patient?: PatientDto) => void,
  getUnpaidBills: (billDate: string) => void
}

interface State {
  cancelTokenSource: CancelTokenSource,
  formData: Partial<FinalFormData>,
  mandateBanks: Array<DropdownOption>,
  mandateBills: Array<MandateBillDto>,
  allChosenState: AllChosenState,
  billReminderPdfModalOpen: boolean,
  savedBillReminders: Array<BillReminderResponse>
}

class AutomaticBillReminderTableView extends React.Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const {options: {workDate}, currentUser} = props;

    const cancelTokenSource = axios.CancelToken.source();

    const mappedBills: Array<ExtendedAutomaticReminderResponse> = this.props.bills.map(bill => {
      return {
        ...bill,
        checked: false
      }
    });

    const formData: Partial<FinalFormData> = {
      reminderDate: workDate,
      bills: mappedBills,
      proofList: false
    };

    this.state = {
      formData: formData,
      mandateBanks: [],
      mandateBills: [],
      allChosenState: AllChosenState.NONE,
      billReminderPdfModalOpen: false,
      savedBillReminders: [],
      cancelTokenSource
    };

    if (currentUser) {

      const {mandateId} = currentUser.userConfig;

      const methods: Array<Promise<any>> = [
        getAllMandateBanksByMandateId(mandateId, cancelTokenSource),
        getAllMandateBillsByMandateId(mandateId, cancelTokenSource)
      ];

      Promise.all(methods)
          .then(responses => {

            const mappedBanks: Array<DropdownOption> = responses[0].map((mandateBank: MandateBankDto) => ({
              key: mandateBank.id,
              text: `${mandateBank.description}, ${mandateBank.city}, (${mandateBank.bankAccount})`,
              value: mandateBank.id
            }));

            this.setState({
              mandateBanks: mappedBanks,
              mandateBills: responses[1],
              formData: {
                ...formData,
                mandateBankId: mappedBanks[0].value
              }
            })
          });

    }

  }

  componentDidUpdate({billsLoading: prevBillsLoading}: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {

    const {formData} = this.state;
    const {bills, billsLoading} = this.props;

    if (prevBillsLoading != billsLoading && !billsLoading) {

      const mappedBills: Array<ExtendedAutomaticReminderResponse> = bills.map(bill => {

        return {
          ...bill,
          checked: false
        }
      });

      this.setState({
        formData: {
          reminderDate: formData.reminderDate,
          mandateBankId: formData.mandateBankId,
          bills: mappedBills
        }
      })
    }
  }

  render(): React.ReactNode {
    const {formData} = this.state;
    return (
        <StyledFinalFormContainer>
          <FinalForm
              onSubmit={this.handleSubmit}
              initialValues={formData}
              decorators={[this.reminderDecorator]}
              mutators={{
                ...arrayMutators
              }}
              subscription={{values: true, pristine: true, submitting: true}}
              render={this.renderForm}
          />
        </StyledFinalFormContainer>
    );
  }

  renderForm = ({submitting, values, handleSubmit, form}: FormRenderProps): JSX.Element => {
    const {billsLoading, getMessage, getMessageWithNamedParams} = this.props;
    const {mandateBanks, mandateBills, billReminderPdfModalOpen, savedBillReminders} = this.state;
    const {selected} = values;
    const bills: Array<ExtendedAutomaticReminderResponse> = values.bills ? values.bills : [];

    const billsToBeReminded = bills.filter(bill => bill.checked).length;
    const billsLength = bills.length;

    const disableSubmit = submitting || billsToBeReminded === 0;

    const reminderInfo = () => {
      const reminderDate = values.reminderDate;
      const date = moment(reminderDate).format("DD.MM.YYYY");

      const mandateBill = mandateBills[selected.numberOfBillReminders + 1];
      let amount = 0;
      let dueDate = date;

      if (mandateBill) {
        dueDate = moment(reminderDate).add(mandateBill.period, "days").format("DD.MM.YYYY");
        amount = mandateBill.surcharge;
      }

      return (
          <ReminderRow>
            <Grid.Column width={10} verticalAlign="middle">
              <Header as="h4">
                {`${selected.numberOfBillReminders + 1}. ${getMessage("bill.reminder")}, ${date}`}
              </Header>
            </Grid.Column>
            <Grid.Column width={6} verticalAlign="middle">
              <Header as="h4">
                {numberFormatter.format(amount)}&emsp;{dueDate}
              </Header>
            </Grid.Column>
          </ReminderRow>
      );
    }

    return (
        <StyledForm onSubmit={handleSubmit}>
          <FormContent>
            <StyledFormContent>
              <StyledContainer style={{width: "60%"}}>
                <Field
                    name="error"
                    component={ErrorMessage}
                    style={errorMessageStyle}
                />
                <Header style={{marginTop: "0"}}>
                  {getMessage("bills")}
                  <StyledSelectedBills>
                    {
                      applyStyles(getMessage("bills.selectedReminderBills")
                          .replace("{{billsToBeReminded}}", "" + billsToBeReminded)
                          .replace("{{billsLength}}", "" + billsLength)
                      )
                    }
                  </StyledSelectedBills>
                </Header>
                <TableContainer>
                  {billsLoading
                      ? <LoaderComponent
                          message={`${getMessage("loading")} ${getMessage("bills").toLowerCase()}`}
                      />
                      : this.renderTable(bills, form)}
                </TableContainer>
                <Header as="h3" textAlign="right">
                  {
                    getMessageWithNamedParams("bills.numberOfBillsToBeReminded", {
                      billsToBeReminded: billsToBeReminded
                    })
                  }
                </Header>
              </StyledContainer>
              <StyledContainer style={{width: "60%"}}>
                <PxGrid>
                  <ReminderDate>
                    <Grid.Column width={6} verticalAlign="middle">{getMessage("reminderDate")}:</Grid.Column>
                    <Grid.Column width={10}>
                      <Field
                          name="reminderDate"
                          component={DatePicker}
                          fluid
                      />
                    </Grid.Column>
                  </ReminderDate>
                  <MandateBank>
                    <Grid.Column width={6} verticalAlign="middle">{getMessage("bill.bank")}:</Grid.Column>
                    <Grid.Column width={10}>
                      <Field
                          name="mandateBankId"
                          component={Select}
                          options={mandateBanks}
                          fluid
                      />
                    </Grid.Column>
                  </MandateBank>
                </PxGrid>
                <PxGrid>
                  <Grid.Row>
                    <Grid.Column verticalAlign="middle">
                      <Header>
                        {getMessage("patient")}
                      </Header>
                    </Grid.Column>
                  </Grid.Row>
                  {selected &&
                  <Grid.Row className={"row-header-data"}>
                    <Grid.Column>
                      <Header as="h4" className={"header-data"}>
                        <div>{`${selected.patientName}, ${selected.patientFirstName}`}</div>
                        <div>{`${selected.patientCity}, ${selected.patientBirthDate}`}</div>
                      </Header>
                    </Grid.Column>
                  </Grid.Row>
                  }
                </PxGrid>
                <PxGrid>
                  <Grid.Row>
                    <Grid.Column verticalAlign="middle">
                      <Header>
                        {getMessage("guarantor")}
                      </Header>
                    </Grid.Column>
                  </Grid.Row>
                  {selected && selected.guarantorDescription &&
                  <Grid.Row className={"row-header-data"}>
                    <Grid.Column>
                      <Header as="h4" className={"header-data"}>
                        <div>{`${selected.guarantorDescription}`}</div>
                        <div>{`${selected.guarantorZip}, ${selected.guarantorCity}`}</div>
                      </Header>
                    </Grid.Column>
                  </Grid.Row>
                  }
                </PxGrid>
                <PxGrid>
                  <Grid.Row>
                    <Grid.Column verticalAlign="middle">
                      <Header>
                        {getMessage("bill.reminderInformation")}
                      </Header>
                    </Grid.Column>
                  </Grid.Row>
                  {selected &&
                  <>
                    <Grid.Row>
                      <Grid.Column width={10} verticalAlign="middle">
                        <Header as="h4">
                          <div>{getMessage("bill.paymentsReceived")}</div>
                        </Header>
                      </Grid.Column>
                      <Grid.Column width={6} verticalAlign="middle">
                        <Header as="h4">
                          <div>{`${numberFormatter.format(selected.paidAmount ? selected.paidAmount : 0)}`}</div>
                        </Header>
                      </Grid.Column>
                    </Grid.Row>
                    {reminderInfo()}
                  </>
                  }
                </PxGrid>
              </StyledContainer>
            </StyledFormContent>
          </FormContent>
          <FormButtonsRow>
            <Button type="submit"
                    compact primary
                    disabled={disableSubmit}
                    onClick={() => this.setAutomaticReminderProoflist(form, false)}
            >
              {getMessage("remind")}
            </Button>
            <Button type="submit"
                    compact
                    secondary
                    disabled={disableSubmit}
                    onClick={() => this.setAutomaticReminderProoflist(form, true)}
            >
              {getMessage("bill.reminderProoflist")}
            </Button>
          </FormButtonsRow>
          {
            billReminderPdfModalOpen &&
            <PdfModal
                modalOpen={billReminderPdfModalOpen}
                onCloseModal={() => this.closeBillReminderPdfModal(values)}
                pdfResponses={savedBillReminders}
                getPdfUrl={this.getBillReminderPdfUrl}
            />
          }
        </StyledForm>
    )
  };

  getBillReminderPdfUrl = (billReminderResponse: BillReminderResponse) => {
    return getBillReminderPdfUrl(billReminderResponse.pdfId, billReminderResponse.pdfName);
  };

  renderTable = (bills: Array<ExtendedAutomaticReminderResponse>, form: FormApi): JSX.Element => {

    const {getMessage} = this.props;

    return (
        <StyledTable
            rowGetter={this.billsRowGetter(bills)}
            rowRenderer={this.billsRowRenderer(form)}
            rowCount={bills.length}
            columns={[
              {
                width: 80,
                flexGrow: 0,
                flexShrink: 0,
                label: this.allChosenHeaderColumn(bills),
                dataKey: "checked",
                cellRenderer: (data: any) => this.checkedCellRenderer(data, form)
              },
              {
                width: 70,
                label: getMessage("bill"),
                dataKey: "status",
                cellRenderer: this.billStatusCellRenderer
              },
              {
                width: 70,
                label: "#",
                dataKey: "id",
                cellRenderer: this.billIdCellRenderer
              },
              {
                width: 100,
                label: getMessage("bill.billDate"),
                dataKey: "billDate",
                cellRenderer: this.billDateCellRenderer("billDate")
              },
              {
                width: 150,
                label: getMessage("value"),
                dataKey: "amount",
                cellRenderer: this.amountCellRenderer
              },
              {
                width: 100,
                label: getMessage("dueDate"),
                dataKey: "dueDate",
                cellRenderer: this.billDateCellRenderer("dueDate")
              }
            ]}
        />
    )
  };

  billsRowGetter = (bills: Array<AutomaticReminderResponse>) => ({index}: any) => {

    if (bills[index]) {
      Object.assign(bills[index], {index: index + 1});

      return bills[index];
    }

    return {
      index: index + 1
    }
  };

  billsRowRenderer = (form: FormApi) => ({className, columns, index, key, style}: any) => {
    const a11yProps = {"aria-rowindex": index + 1};

    const {bills = []}: Partial<FinalFormData> = form.getState().values;

    let styles = {...style};

    if (bills[index] && bills[index].checked) {
      styles = {
        ...styles,
        ...activeRowStyle
      }
    }

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={styles}
            onClick={() => form.change("selected", bills[index])}
        >
          {columns}
        </div>
    );
  };


  billStatusCellRenderer = (data: any) => {

    const {bills} = this.props;

    const rowData: BillReminderSearchResponse = data.rowData;
    const rowIndex = data.rowIndex;

    const billIconRequest: BillIconRequest = {
      hasReminders: bills[rowIndex] ? bills[rowIndex].numberOfBillReminders > 0 : false,
      status: rowData.status as BillStatus,
      type: rowData.type as BillType,
      cashBill: rowData.cashBill
    };

    return <div>{getBillIcon(billIconRequest)}</div>
  };

  billIdCellRenderer = (data: any) => {

    const rowData: AutomaticReminderResponse = data.rowData;

    if (rowData.numberOfBillReminders !== undefined) {
      const asterisk = "*";
      return `${rowData.id}${asterisk.repeat(rowData.numberOfBillReminders + 1)}`
    }

    return rowData.id;
  };

  billDateCellRenderer = (field: string) => (data: any) => <div>{data.rowData[field]}</div>;

  amountCellRenderer = (data: any) =>
      <div style={numberColumnStyle}>{currencyFormatter.format(data.rowData.amount)}</div>;

  checkedCellRenderer = (data: any, form: FormApi) => {
    const {rowData} = data;

    return (
        <Checkbox checked={rowData.checked}
                  label={rowData.billId}
                  onClick={() => this.toggleBill(rowData, form)}
        />
    )
  };

  allChosenHeaderColumn = (bills: Array<ExtendedAutomaticReminderResponse>) => {

    const allChosenState = this.state.allChosenState;

    const countBills = bills.length;

    return (
        <CheckboxSc
            checked={allChosenState === AllChosenState.ALL}
            indeterminate={allChosenState === AllChosenState.SOME}
            onChange={
              () => {
                const currentState = this.state.allChosenState;
                let nextState = AllChosenState.NONE;
                let bills: Array<ExtendedAutomaticReminderResponse>;
                if (currentState === AllChosenState.ALL || currentState === AllChosenState.SOME) {
                  nextState = AllChosenState.NONE;
                  bills = this.state.formData.bills!.map(bill => ({...bill, checked: false}));
                } else {
                  nextState = AllChosenState.ALL;
                  bills = this.state.formData.bills!.map(bill => ({...bill, checked: true}));
                }

                const formData = {
                  ...this.state.formData,
                  bills
                };

                this.setState({
                  allChosenState: nextState,
                  formData
                })
              }
            }
            disabled={countBills === 0}
        />
    )
  };

  toggleBill = (rowData: ExtendedAutomaticReminderResponse, form: FormApi) => {
    const stateBills: Array<ExtendedAutomaticReminderResponse> = this.state.formData.bills!;
    const bills = stateBills.map(
        bill => (bill.id !== rowData.id) ? bill : {...bill, checked: !bill.checked});

    const chosenBill = bills.find(bill => bill.checked);
    const unchosenBill = bills.find(bill => !bill.checked);

    let allChosenState = AllChosenState.SOME;
    if (!unchosenBill) {
      allChosenState = AllChosenState.ALL;
    } else if (!chosenBill) {
      allChosenState = AllChosenState.NONE;
    }

    const formData = {
      ...this.state.formData,
      bills
    };

    form.change("selected", rowData);

    this.setState({
      formData,
      allChosenState
    });
  };

  reminderDecorator = createDecorator({
    field: "reminderDate",
    updates: (...params) => {
      const {reminderDate} = params[2] as FinalFormData;

      this.props.getUnpaidBills(reminderDate);

      return {};
    }
  });

  setAutomaticReminderProoflist = (form: FormApi, proofList: boolean) => {
    form.change("proofList", proofList);
  }

  handleSubmit = async (values: Partial<FinalFormData>) => {
    const {currentUser} = this.props;
    const {cancelTokenSource} = this.state;

    let checkedBills: Array<ExtendedAutomaticReminderResponse> = values.bills ?
        values.bills.filter(x => x.checked) :
        [];

    let request: Partial<AutomaticReminderSubmit> = {
      mandateBankId: values.mandateBankId,
      reminderDate: values.reminderDate,
      billIds: checkedBills.map(bill => bill.id),
      mandateId: currentUser!.userConfig.mandateId,
      proofList: values.proofList
    };

    automaticRemind(request, cancelTokenSource).then(response => {
      const {history, currentPatient} = this.props;

      const refreshPatient = () => this.props.onPatientRefresh(currentPatient);

      if (response.length > 0) {
        this.openBillReminderPdfModal(response);
      } else {
        history.push("/");
      }
      if (currentPatient && !values.proofList) {
        refreshPatient();
      }

    });
  };

  openBillReminderPdfModal = (billReminderResponses: Array<BillReminderResponse>) => {
    this.setState({
      billReminderPdfModalOpen: true,
      savedBillReminders: billReminderResponses
    })
  };

  closeBillReminderPdfModal = (values: Partial<FinalFormData>) => {
    const {history} = this.props;

    this.setState({
      billReminderPdfModalOpen: false,
      savedBillReminders: []
    });

    if (!values.proofList) {
      history.push("/");
    }
  };

}

export default withMessages(withAuthContext(withRouterWorkaround(AutomaticBillReminderTableView)));
